import { useState, useEffect } from 'react';
import { catalogService } from '../../services/catalogService';
import type { Catalog } from '../../types/catalog';
import { useConfirm } from '../common/components/useConfirm';
import { MIN_SEARCH_LENGTH } from '../../utils/searchConstants';
import { clearEditCatalogDraft } from '../../utils/catalogDraftStorage';

interface UseCatalogDataParams {
  companyId: number | null;
}

export function useCatalogData({ companyId }: UseCatalogDataParams) {
  const { confirm, confirmDialog } = useConfirm();

  const [catalogs, setCatalogs] = useState<Catalog[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterName, setFilterName] = useState('');
  const [debouncedName, setDebouncedName] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  // Debounce filter
  useEffect(() => {
    if (filterName.length > 0 && filterName.length < MIN_SEARCH_LENGTH) return;
    const timer = setTimeout(() => {
      setDebouncedName(filterName);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterName]);

  // Fetch catalogs
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!companyId) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await catalogService.search(companyId, {
          name: debouncedName || undefined,
          includeArchived: !showOnlyActive,
          page: page - 1,
          size: 10,
        });
        if (cancelled) {
          return;
        }
        setCatalogs(response.content);
        setTotalPages(response.totalPages);
        setError(null);
      } catch (err) {
        console.error('Error fetching catalogs:', err);
        if (!cancelled) {
          setError('Не вдалося завантажити каталоги');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setIsFirstLoad(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [companyId, page, debouncedName, showOnlyActive]);

  const handleDeleteCatalog = async (catalogId: number) => {
    if (!(await confirm('Ви впевнені, що хочете архівувати цей каталог?'))) return;

    await catalogService.delete(catalogId);
    if (companyId) {
      clearEditCatalogDraft(companyId, catalogId);
    }
    setCatalogs((prev) => (
      showOnlyActive
        ? prev.filter((c) => c.id !== catalogId)
        : prev.map((c) => (c.id === catalogId ? { ...c, deactivatedDate: new Date().toISOString() } : c))
    ));
  };

  const handleRestoreCatalog = async (catalogId: number) => {
    await catalogService.restore(catalogId);
    setCatalogs((prev) => prev.map((c) => (
      c.id === catalogId ? { ...c, deactivatedDate: undefined } : c
    )));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleShowOnlyActiveChange = (value: boolean) => {
    setShowOnlyActive(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterName('');
    handleShowOnlyActiveChange(true);
  };

  return {
    data: {
      catalogs,
      loading,
      isFirstLoad,
      error,
    },
    pagination: {
      page,
      count: totalPages,
      onChange: handlePageChange,
    },
    filters: {
      filterName,
      setFilterName,
      debouncedName,
      showFilters,
      setShowFilters,
      showOnlyActive,
      setShowOnlyActive: handleShowOnlyActiveChange,
      handleClearFilters,
      hasActiveFilters: !!filterName || !showOnlyActive,
    },
    crud: {
      handleDeleteCatalog,
      handleRestoreCatalog,
    },
    dialogs: {
      confirmDialog,
    },
  };
}
